import { useState } from 'react'

import { FileApiType } from '@/api/shared/types'
import { UploadedFileType } from '@/components/shared/file-uploader'
import {
  downloadFileByLink,
  getFileExtensionByPath,
  IMAGE_EXTENSIONS,
  SVG_EXTENSION
} from '@/utils/file'

import { FileType } from '../lib/requests.types'
import { checkIsImageFile } from '../lib/requests.utils'

const isRemoteFileType = (
  files: FileApiType[] | UploadedFileType[],
  fileType: FileType
): files is FileApiType[] => {
  return fileType === FileType.Remote
}

const isLocalFileType = (
  files: FileApiType[] | UploadedFileType[],
  fileType: FileType
): files is UploadedFileType[] => {
  return fileType === FileType.Local
}

const getUploadedFilePreviewLink = (file: UploadedFileType): string => {
  if (file.file.size > 0) {
    return URL.createObjectURL(file.file)
  }

  return file.url ?? URL.createObjectURL(file.file)
}

const processLocalFiles = (files: UploadedFileType[]): FileApiType[] => {
  return files
    .filter(file => {
      const fileExtension = getFileExtensionByPath(file.file.name || '')
      return IMAGE_EXTENSIONS.includes(`.${fileExtension}`)
    })
    .map(file => ({
      name: file.file.name,
      link: getUploadedFilePreviewLink(file)
    }))
}

const processRemoteFiles = async (
  files: FileApiType[]
): Promise<FileApiType[]> => {
  const validationResults = []

  for (const file of files) {
    if (
      (await checkIsImageFile(file.link)) &&
      !file.name.includes(SVG_EXTENSION)
    ) {
      validationResults.push(file)
    }
  }

  return validationResults
}

export const useFilePreview = (
  files: FileApiType[] | UploadedFileType[],
  fileType: FileType
) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentFiles, setCurrentFiles] = useState<FileApiType[]>([])
  const [activeIndex, setActiveIndex] = useState(0)

  const handleFileAction = async (
    name: string,
    image?: string,
    file?: UploadedFileType
  ): Promise<void> => {
    let validFiles: FileApiType[] = []
    let index = 0

    if (isLocalFileType(files, fileType)) {
      validFiles = processLocalFiles(files)
      index = validFiles.findIndex(file => file.name === name)
    } else if (isRemoteFileType(files, fileType)) {
      validFiles = await processRemoteFiles(files)
      index = validFiles.findIndex(file => file.link === image)
    }

    if (validFiles.length > 0 && index >= 0) {
      setCurrentFiles(validFiles)
      setActiveIndex(index)
      setIsModalOpen(true)
    } else {
      downloadFileByLink(name, image ?? file)
    }
  }

  const handleCloseModal = (): void => {
    setIsModalOpen(false)
    setCurrentFiles([])
  }

  return {
    isModalOpen,
    currentFiles,
    activeIndex,
    handleFileAction,
    handleCloseModal
  }
}
